import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { finalize, map, share, shareReplay, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Company } from "../../models/company";
import { CosmosCustomer } from "../../models/customer";
import { CustomerSummary } from "../../models/customer-summary";
import { ODataResponse } from "../../models/odata-response";
import { escapeODataString } from "../../odata-utils";
import { ICustomerService } from "./customer.service.interface";
import { AgentSalesSalesDistricts } from "../../constants/agent-sales-sales-districts";

@Injectable()
export class CustomerService implements ICustomerService {
    private static readonly customerLookupBatchSize = 25;
    private baseUrl = "";
    private currentCompany: Company = null;
    private readonly customersByCode = new Map<string, CosmosCustomer>();
    private readonly customerSummariesByCode = new Map<string, CustomerSummary>();
    private readonly customerRequestsByCode = new Map<string, Observable<CosmosCustomer>>();

    constructor(private httpClient: HttpClient) {
        this.baseUrl = environment.apiBaseUrl;
    }

    public getCustomerByCode(axCode: string): Observable<CosmosCustomer> {
        const normalizedAxCode = this.normalizeAxCode(axCode);
        if (!normalizedAxCode) {
            return of(null);
        }

        const cachedCustomer = this.customersByCode.get(normalizedAxCode);
        if (cachedCustomer) {
            return of(cachedCustomer);
        }

        const inFlightRequest = this.customerRequestsByCode.get(normalizedAxCode);
        if (inFlightRequest) {
            return inFlightRequest;
        }

        const params = new HttpParams()
            .set('$filter', `AXCode eq '${escapeODataString(normalizedAxCode)}'`)
            .set('$top', '1');
        const request = this.httpClient.get<ODataResponse<CosmosCustomer[]>>(this.baseUrl + "customers", { params }).pipe(
            map(response => {
                const customer = response?.value?.[0] ?? null;
                if (customer) {
                    this.cacheCustomer(customer);
                }
                return customer;
            }),
            finalize(() => this.customerRequestsByCode.delete(normalizedAxCode)),
            shareReplay(1)
        );
        this.customerRequestsByCode.set(normalizedAxCode, request);

        return request;
    }

    public getCustomersByCodes(axCodes: string[]): Observable<CustomerSummary[]> {
        const normalizedAxCodes = this.normalizeAxCodes(axCodes);
        if (normalizedAxCodes.length === 0) {
            return of([]);
        }

        const missingAxCodes = normalizedAxCodes.filter(axCode => !this.customerSummariesByCode.has(axCode));
        if (missingAxCodes.length === 0) {
            return of(this.getOrderedCustomerSummaries(normalizedAxCodes));
        }

        const batches: string[][] = [];
        for (let index = 0; index < missingAxCodes.length; index += CustomerService.customerLookupBatchSize) {
            batches.push(missingAxCodes.slice(index, index + CustomerService.customerLookupBatchSize));
        }

        const requests = batches.map(batch => {
            const filter = `(${batch.map(axCode => `AXCode eq '${escapeODataString(axCode)}'`).join(' or ')})`;
            const params = new HttpParams()
                .set('$select', 'AXCode,CustomerName')
                .set('$filter', filter)
                .set('$top', batch.length.toString());

            return this.httpClient.get<ODataResponse<CustomerSummary[]>>(this.baseUrl + "customers", { params }).pipe(
                map(response => response?.value ?? []),
                tap(customers => customers.forEach(customer => this.cacheCustomerSummary(customer)))
            );
        });

        return forkJoin(requests).pipe(map(() => this.getOrderedCustomerSummaries(normalizedAxCodes)));
    }

    private company$: Observable<Company>;

    public getCompanyByCustomerRegisterCode(companyRegisterCode: string): Observable<Company> {
        if (this.currentCompany && this.currentCompany?.bkCompanyKey == companyRegisterCode) {
            return of(this.currentCompany);
        }

        if (this.company$) {
            return this.company$;
        }

        const url = this.baseUrl + `companydatacosmos?$top=1&$filter=bkCompanyKey eq '` + companyRegisterCode + `'`;

        this.company$ = this.httpClient.get<ODataResponse<Company>>(url).pipe(map(response => {
            if (!response || !response.value || !response.value[0]) {
                throw new Error('alerts.noCompanyForCustomerFound');
            }
            this.currentCompany = response.value[0];
            return response.value[0] as Company;
        }),
        share(),
        finalize(() => this.company$ = null));

        return this.company$;
    }

    public isAgentSalesCustomer(axCode: string): Observable<boolean> {
        return this.getCustomerByCode(axCode).pipe(map(customer => {
            return customer && AgentSalesSalesDistricts.includes(customer.SalesDistrict);
        }));
    }

    private cacheCustomer(customer: CosmosCustomer): void {
        const normalizedAxCode = this.normalizeAxCode(customer?.AXCode);
        if (!normalizedAxCode) {
            return;
        }

        this.customersByCode.set(normalizedAxCode, customer);
        this.cacheCustomerSummary(customer);
    }

    private cacheCustomerSummary(customer: CustomerSummary): void {
        const normalizedAxCode = this.normalizeAxCode(customer?.AXCode);
        if (!normalizedAxCode) {
            return;
        }

        this.customerSummariesByCode.set(normalizedAxCode, {
            AXCode: normalizedAxCode,
            CustomerName: customer.CustomerName
        });
    }

    private getOrderedCustomerSummaries(axCodes: string[]): CustomerSummary[] {
        return axCodes.map(axCode => this.customerSummariesByCode.get(axCode) ?? {
            AXCode: axCode,
            CustomerName: axCode
        });
    }

    private normalizeAxCodes(axCodes: string[]): string[] {
        return [...new Set((axCodes ?? []).map(axCode => this.normalizeAxCode(axCode)).filter(Boolean))];
    }

    private normalizeAxCode(axCode: string): string {
        return axCode?.trim().toUpperCase() ?? '';
    }
}
